import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AgentLayout from "../components/AgentLayout";
import NavButton from "../components/NavButton";
import axiosInstance from "../../../utils/axiosInstance";

const ManageDistributors = () => {
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.userStore);
  const userInitialized = userInfo.initialized;
  const canManageDistributors = [1, 2].includes(
    Number(userInfo.data?.user_role)
  );
  const [distributors, setDistributors] = useState([]);
  const [distributorName, setDistributorName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [message, setMessage] = useState({ text: "", isError: false });

  const fetchDistributors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/company/distributors");
      setDistributors(response.data?.distributors || []);
    } catch (error) {
      setMessage({
        text: error.response?.data?.message || "Failed to load distributors",
        isError: true,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!userInitialized) return;
    if (!canManageDistributors) {
      navigate("/agent/accounts/chart", { replace: true });
    }
  }, [userInitialized, canManageDistributors, navigate]);

  useEffect(() => {
    if (!canManageDistributors) return;
    fetchDistributors();
  }, [canManageDistributors, fetchDistributors]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = distributorName.trim();
    if (!name) {
      setMessage({ text: "Distributor name is required", isError: true });
      return;
    }

    setSaving(true);
    setMessage({ text: "", isError: false });
    try {
      const response = await axiosInstance.post("/company/distributors", {
        distributor_name: name,
      });
      setMessage({
        text: response.data?.message || "Distributor added",
        isError: false,
      });
      setDistributorName("");
      await fetchDistributors();
    } catch (error) {
      setMessage({
        text: error.response?.data?.message || "Failed to add distributor",
        isError: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this distributor?")) return;

    setDeletingId(id);
    setMessage({ text: "", isError: false });
    try {
      const response = await axiosInstance.delete(`/company/distributors/${id}`);
      setMessage({
        text: response.data?.message || "Distributor deleted",
        isError: false,
      });
      setDistributors((current) => current.filter((row) => row._id !== id));
    } catch (error) {
      setMessage({
        text: error.response?.data?.message || "Failed to delete distributor",
        isError: true,
      });
    } finally {
      setDeletingId("");
    }
  };

  if (!userInitialized || !canManageDistributors) {
    return null;
  }

  return (
    <AgentLayout>
      <div className="container-fluid">
        <div className="bets-section-wrapper">
          <NavButton />

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <i className="fa fa-align-justify"></i>
                  <strong className="site_family"> Add Distributor</strong>
                </div>
                <div className="card-body">
                  <form onSubmit={handleAdd}>
                    <div className="form-group row">
                      <label className="col-4 col-md-2 col-form-label site_family">
                        Distributor Name
                      </label>
                      <div className="col-8 col-md-6">
                        <input
                          className="form-control"
                          type="text"
                          value={distributorName}
                          onChange={(e) => setDistributorName(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-8 offset-4 offset-md-2">
                        <button
                          type="submit"
                          className="btn btn-success site_family"
                          style={{ background: "#02b181" }}
                          disabled={saving}
                        >
                          <strong>{saving ? "Saving..." : "Add"}</strong>
                        </button>
                        <Link
                          to="/agent/accounts/chart"
                          className="btn btn-secondary site_family"
                          style={{ marginLeft: "8px" }}
                        >
                          Back
                        </Link>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {message.text && (
            <div
              className={`alert ${
                message.isError ? "alert-danger" : "alert-success"
              } site_family`}
              style={{ marginTop: "10px" }}
            >
              {message.text}
            </div>
          )}

          <div className="row" style={{ marginTop: "15px" }}>
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <i className="fa fa-align-justify"></i>
                  <strong className="site_family"> Distributors</strong>
                </div>
                <div className="card-body" style={{ overflowX: "auto" }}>
                  {loading ? (
                    <div className="site_family">Loading...</div>
                  ) : (
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          <th className="site_family">Distributor Name</th>
                          <th className="site_family">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {distributors.length === 0 ? (
                          <tr>
                            <td colSpan={2} className="site_family">
                              No distributors found
                            </td>
                          </tr>
                        ) : (
                          distributors.map((row) => (
                            <tr key={row._id}>
                              <td className="site_family">
                                {row.distributor_name}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger site_family"
                                  onClick={() => handleDelete(row._id)}
                                  disabled={deletingId === row._id}
                                >
                                  {deletingId === row._id
                                    ? "Deleting..."
                                    : "Delete"}
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AgentLayout>
  );
};

export default ManageDistributors;
